import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';
import { AccountInfo } from '../types/auth';
import { validateUsername } from '../utils/validation';
import Spinner from './Spinner';

const Account: React.FC = () => {
    const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Username update state
    const [newUsername, setNewUsername] = useState('');
    const [usernameLoading, setUsernameLoading] = useState(false);
    const [usernameValidation, setUsernameValidation] = useState<{
        isValid: boolean;
        error?: string;
    }>({ isValid: true });

    // Password change state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);

    // Subscription management state
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const maxCancelReasonLength = 500;

    const navigate = useNavigate();

    useEffect(() => {
        loadAccountInfo();
    }, []);

    const loadAccountInfo = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await authApi.getAccountInfo();

            if (response.status === 'success' && response.account) {
                setAccountInfo(response.account);
                setNewUsername(response.account.username);
            } else {
                setError(response.message || 'Failed to load account information');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setNewUsername(value);

        // Clear error when username is changed
        setError('');

        // Real-time validation for username using common validation function
        if (value.trim()) {
            const validation = validateUsername(value);
            setUsernameValidation(validation);
        } else {
            setUsernameValidation({ isValid: true });
        }
    };

    const handleUsernameUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!accountInfo || newUsername === accountInfo.username) return;

        // Validate username before submission
        const validation = validateUsername(newUsername);
        if (!validation.isValid) {
            setError(validation.error!);
            return;
        }

        try {
            setUsernameLoading(true);
            setError('');
            setSuccess('');

            const response = await authApi.updateUsername({ name: newUsername });

            if (response.status === 'success') {
                setSuccess('Username updated successfully');
                setAccountInfo(prev => (prev ? { ...prev, username: newUsername } : null));
            } else {
                setError(response.message || 'Failed to update username');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setUsernameLoading(false);
        }
    };

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            setError('New passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        try {
            setPasswordLoading(true);
            setError('');
            setSuccess('');

            const response = await authApi.changePassword({
                currentPassword,
                newPassword,
                confirmPassword,
            });

            if (response.status === 'success') {
                setSuccess('Password changed successfully');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                setError(response.message || 'Failed to change password');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleCancelSubscription = () => {
        setError('');
        setSuccess('');
        setCancelReason('');
        setShowCancelModal(true);
    };

    const confirmCancelSubscription = async () => {
        try {
            setSubscriptionLoading(true);
            setError('');
            setSuccess('');

            const trimmedReason = cancelReason.trim().slice(0, maxCancelReasonLength);
            const response = await authApi.cancelSubscription({ reason: trimmedReason });

            if (response.status === 'success') {
                setSuccess('Subscription cancelled successfully');
                setShowCancelModal(false);
                loadAccountInfo(); // Refresh account info
            } else {
                setError(response.message || 'Failed to cancel subscription');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setSubscriptionLoading(false);
        }
    };

    const handleReactivateSubscription = async () => {
        try {
            setSubscriptionLoading(true);
            setError('');
            setSuccess('');

            const response = await authApi.reactivateSubscription();

            if (response.status === 'success') {
                setSuccess('Subscription reactivated successfully');
                loadAccountInfo(); // Refresh account info
            } else {
                setError(response.message || 'Failed to reactivate subscription');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setSubscriptionLoading(false);
        }
    };

    const handleSignUp = () => {
        navigate('/signup-existing');
    };

    const handleExpiredSubscriptionSignUp = () => {
        navigate('/signup-existing?returnTo=account');
    };

    const handleUpdatePayment = async () => {
        try {
            setError('');
            setSuccess('');

            const response = await authApi.getPaymentPortalUrl();

            if (response.status === 'success' && response.portalSessionURL) {
                window.open(response.portalSessionURL, '_blank');
            } else {
                setError(response.message || 'Failed to open payment portal');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        }
    };

    const getSubscriptionStatusColor = (status: string) => {
        switch (status) {
            case 'active':
            case 'trial':
                return 'text-white bg-green-500';
            case 'cancelled':
                return 'text-white bg-yellow-500';
            case 'expired':
                return 'text-white bg-red-500';
            default:
                return 'text-white bg-gray-500';
        }
    };

    const getSubscriptionStatusText = (status: string) => {
        switch (status) {
            case 'active':
                return 'Active';
            case 'trial':
                return 'Trial';
            case 'cancelled':
                return 'Cancelled';
            case 'expired':
                return 'Expired';
            default:
                return 'No Subscription';
        }
    };

    if (loading) {
        return (
            <div
                className='min-h-screen flex items-center justify-center'
                style={{ backgroundColor: '#1D1A31' }}
            >
                <div className='flex flex-col items-center'>
                    <Spinner size='lg' color='blue' />
                    <p className='mt-4 text-white'>Loading account information...</p>
                </div>
            </div>
        );
    }

    // Show error message at the top if there's an error and no account info
    const showTopError = !accountInfo && error;

    return (
        <div className='min-h-screen py-8' style={{ backgroundColor: '#1D1A31' }}>
            {/* Sticky Header */}
            <div className='sticky top-0 z-40 -mt-8 mb-8'>
                <div className='bg-[#1D1A31]/95 border-b border-gray-800'>
                    <div className='max-w-4xl mx-auto px-4 py-3 flex items-center justify-between'>
                        <button
                            onClick={() =>
                                (window.location.href = `${
                                    import.meta.env.VITE_REACT_APP_GAME_URL
                                }?t=${new Date().getTime()}`)
                            }
                            className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black py-2 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                        >
                            ← Back to Puzzle Planet
                        </button>
                        <span className='text-sm text-gray-300'>Account</span>
                    </div>
                </div>
            </div>

            <div className='max-w-4xl mx-auto px-4'>
                {/* Header */}
                <div className='text-center mb-8'>
                    <h1 className='text-4xl font-bold text-orange-500 mb-2'>Account Settings</h1>
                    <p className='text-lg text-gray-400'>Manage your account and subscription</p>
                </div>

                {/* Success/Error Messages */}
                {success && (
                    <div className='mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg'>
                        <div className='flex items-center'>
                            <svg
                                className='w-5 h-5 mr-2'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                            >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth='2'
                                    d='M5 13l4 4L19 7'
                                ></path>
                            </svg>
                            {success}
                        </div>
                    </div>
                )}

                {showTopError && (
                    <div className='mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg'>
                        <div className='flex items-center justify-between'>
                            <div className='flex items-center'>
                                <svg
                                    className='w-5 h-5 mr-2'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                    ></path>
                                </svg>
                                {error}
                            </div>
                            <button
                                onClick={loadAccountInfo}
                                className='ml-4 bg-gradient-to-r from-yellow-400 to-orange-500 !text-black px-4 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium text-sm'
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                )}

                {error && !showTopError && (
                    <div className='mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg'>
                        <div className='flex items-center'>
                            <svg
                                className='w-5 h-5 mr-2'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                            >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth='2'
                                    d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                ></path>
                            </svg>
                            {error}
                        </div>
                    </div>
                )}

                <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
                    {/* Account Information */}
                    <div className='bg-white rounded-2xl shadow-xl border border-gray-100 p-8 flex flex-col'>
                        <h2 className='text-2xl font-semibold text-gray-900 mb-6'>
                            Account Information
                        </h2>

                        {accountInfo ? (
                            <div className='flex flex-col flex-grow space-y-4'>
                                {/* Email (read-only) */}
                                <div>
                                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                                        Email Address
                                    </label>
                                    <input
                                        type='email'
                                        value={accountInfo.email}
                                        disabled
                                        className='w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed'
                                    />
                                </div>

                                {/* Username Update */}
                                <form
                                    onSubmit={handleUsernameUpdate}
                                    className='flex flex-col flex-grow space-y-4'
                                >
                                    <div>
                                        <label
                                            htmlFor='username'
                                            className='block text-sm font-medium text-gray-700 mb-2'
                                        >
                                            Username
                                        </label>
                                        <input
                                            type='text'
                                            id='username'
                                            value={newUsername}
                                            onChange={handleUsernameChange}
                                            disabled={usernameLoading}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-offset-0 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed ${
                                                newUsername.trim() && !usernameValidation.isValid
                                                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                    : newUsername.trim() &&
                                                      usernameValidation.isValid
                                                    ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                                                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                            }`}
                                            placeholder='Choose a username (4-20 chars, letters & numbers only)'
                                        />
                                        {/* Username validation message */}
                                        {newUsername.trim() && !usernameValidation.isValid && (
                                            <p className='mt-1 text-sm text-red-600'>
                                                {usernameValidation.error}
                                            </p>
                                        )}
                                    </div>
                                    <div className='flex-grow'></div>
                                    <div>
                                        <button
                                            type='submit'
                                            disabled={
                                                usernameLoading ||
                                                newUsername === accountInfo.username ||
                                                !usernameValidation.isValid
                                            }
                                            className='w-full bg-gradient-to-r from-yellow-400 to-orange-500 !text-black py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium disabled:transform-none'
                                        >
                                            {usernameLoading ? (
                                                <div className='flex items-center justify-center'>
                                                    <Spinner
                                                        size='sm'
                                                        color='black'
                                                        className='mr-2'
                                                    />
                                                    Updating...
                                                </div>
                                            ) : (
                                                'Update Username'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        ) : (
                            <div className='text-center py-8'>
                                <div className='text-gray-400 mb-4'>
                                    <svg
                                        className='w-12 h-12 mx-auto'
                                        fill='none'
                                        stroke='currentColor'
                                        viewBox='0 0 24 24'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth='2'
                                            d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
                                        ></path>
                                    </svg>
                                </div>
                                <p className='text-gray-500 mb-4'>
                                    Account information not available
                                </p>
                                <button
                                    onClick={loadAccountInfo}
                                    className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black px-4 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                                >
                                    Load Account Info
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Password Change */}
                    <div className='bg-white rounded-2xl shadow-xl border border-gray-100 p-8 flex flex-col'>
                        <h2 className='text-2xl font-semibold text-gray-900 mb-6'>
                            Change Password
                        </h2>

                        <form
                            onSubmit={handlePasswordChange}
                            className='flex flex-col flex-grow space-y-4'
                        >
                            <div>
                                <label
                                    htmlFor='currentPassword'
                                    className='block text-sm font-medium text-gray-700 mb-2'
                                >
                                    Current Password
                                </label>
                                <input
                                    type='password'
                                    id='currentPassword'
                                    value={currentPassword}
                                    onChange={e => setCurrentPassword(e.target.value)}
                                    disabled={passwordLoading}
                                    required
                                    className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                                    placeholder='Enter current password'
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor='newPassword'
                                    className='block text-sm font-medium text-gray-700 mb-2'
                                >
                                    New Password
                                </label>
                                <input
                                    type='password'
                                    id='newPassword'
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    disabled={passwordLoading}
                                    required
                                    className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                                    placeholder='Enter new password'
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor='confirmPassword'
                                    className='block text-sm font-medium text-gray-700 mb-2'
                                >
                                    Confirm New Password
                                </label>
                                <input
                                    type='password'
                                    id='confirmPassword'
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    disabled={passwordLoading}
                                    required
                                    className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                                    placeholder='Confirm new password'
                                />
                            </div>

                            <div className='flex-grow'></div>
                            <div>
                                <button
                                    type='submit'
                                    disabled={
                                        passwordLoading ||
                                        !currentPassword ||
                                        !newPassword ||
                                        !confirmPassword
                                    }
                                    className='w-full bg-gradient-to-r from-yellow-400 to-orange-500 !text-black py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium disabled:transform-none'
                                >
                                    {passwordLoading ? (
                                        <div className='flex items-center justify-center'>
                                            <Spinner size='sm' color='black' className='mr-2' />
                                            Changing...
                                        </div>
                                    ) : (
                                        'Change Password'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Subscription Management */}
                <div className='mt-8 bg-white rounded-2xl shadow-xl border border-gray-100 p-8'>
                    <h2 className='text-2xl font-semibold text-gray-900 mb-6'>Subscription</h2>

                    {accountInfo ? (
                        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
                            <div className='flex items-center space-x-4'>
                                <div
                                    className={`px-4 py-2 rounded-full text-sm font-medium ${getSubscriptionStatusColor(
                                        accountInfo.subscriptionStatus
                                    )}`}
                                >
                                    {getSubscriptionStatusText(accountInfo.subscriptionStatus)}
                                </div>
                                {accountInfo.subscriptionEndDate && (
                                    <span className='text-sm text-gray-600'>
                                        {accountInfo.subscriptionStatus === 'active'
                                            ? 'Renews'
                                            : 'Expires'}{' '}
                                        on{' '}
                                        {new Date(
                                            accountInfo.subscriptionEndDate
                                        ).toLocaleDateString()}
                                    </span>
                                )}
                            </div>

                            <div className='flex flex-wrap gap-3'>
                                {accountInfo.subscriptionStatus === 'none' && (
                                    <button
                                        onClick={handleSignUp}
                                        className='bg-gradient-to-r from-green-600 to-emerald-600 !text-black px-6 py-2 rounded-lg hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                                    >
                                        Sign Up
                                    </button>
                                )}

                                {accountInfo.subscriptionStatus === 'active' && (
                                    <>
                                        <button
                                            onClick={handleUpdatePayment}
                                            className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black px-6 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                                        >
                                            Update Payment
                                        </button>
                                        <button
                                            onClick={handleCancelSubscription}
                                            disabled={subscriptionLoading}
                                            className='bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-2 rounded-lg hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium disabled:transform-none'
                                        >
                                            {subscriptionLoading ? (
                                                <div className='flex items-center'>
                                                    <Spinner
                                                        size='sm'
                                                        color='black'
                                                        className='mr-2'
                                                    />
                                                    Cancelling...
                                                </div>
                                            ) : (
                                                'Cancel Subscription'
                                            )}
                                        </button>
                                    </>
                                )}

                                {accountInfo.subscriptionStatus === 'cancelled' && (
                                    <>
                                        <button
                                            onClick={handleUpdatePayment}
                                            className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black px-6 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                                        >
                                            Update Payment
                                        </button>
                                        <button
                                            onClick={handleReactivateSubscription}
                                            disabled={subscriptionLoading}
                                            className='bg-gradient-to-r from-green-600 to-emerald-600 !text-black px-6 py-2 rounded-lg hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium disabled:transform-none'
                                        >
                                            {subscriptionLoading ? (
                                                <div className='flex items-center'>
                                                    <Spinner
                                                        size='sm'
                                                        color='black'
                                                        className='mr-2'
                                                    />
                                                    Reactivating...
                                                </div>
                                            ) : (
                                                'Reactivate'
                                            )}
                                        </button>
                                    </>
                                )}

                                {accountInfo.subscriptionStatus === 'expired' && (
                                    <button
                                        onClick={handleExpiredSubscriptionSignUp}
                                        className='bg-gradient-to-r from-green-600 to-emerald-600 !text-black px-6 py-2 rounded-lg hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                                    >
                                        Renew Subscription
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className='text-center py-8'>
                            <div className='text-gray-400 mb-4'>
                                <svg
                                    className='w-12 h-12 mx-auto'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z'
                                    ></path>
                                </svg>
                            </div>
                            <p className='text-gray-500 mb-4'>
                                Subscription information not available
                            </p>
                            <button
                                onClick={loadAccountInfo}
                                className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black px-4 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                            >
                                Load Subscription Info
                            </button>
                        </div>
                    )}
                </div>

                {showCancelModal && (
                    <div className='fixed inset-0 z-50 flex items-center justify-center'>
                        <div className='absolute inset-0 bg-black opacity-50'></div>
                        <div className='relative bg-white rounded-2xl shadow-xl border border-gray-100 p-6 w-full max-w-lg mx-4'>
                            <h3 className='text-xl font-semibold text-gray-900 mb-2'>
                                We’re sorry to see you go
                            </h3>
                            <p className='text-sm text-gray-600 mb-4'>
                                Please tell us why you want to cancel (optional, max 500
                                characters).
                            </p>
                            <textarea
                                value={cancelReason}
                                onChange={e =>
                                    setCancelReason(e.target.value.slice(0, maxCancelReasonLength))
                                }
                                maxLength={maxCancelReasonLength}
                                rows={5}
                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200'
                                placeholder='Your feedback helps us improve'
                            />
                            <div className='mt-2 text-xs text-gray-500 text-right'>
                                {cancelReason.length}/{maxCancelReasonLength}
                            </div>
                            <div className='mt-4 flex justify-end gap-3'>
                                <button
                                    onClick={() => setShowCancelModal(false)}
                                    disabled={subscriptionLoading}
                                    className='px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    Never mind
                                </button>
                                <button
                                    onClick={confirmCancelSubscription}
                                    disabled={subscriptionLoading}
                                    className='bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-2 rounded-lg hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium disabled:transform-none'
                                >
                                    {subscriptionLoading ? (
                                        <div className='flex items-center'>
                                            <Spinner size='sm' color='black' className='mr-2' />
                                            Cancelling...
                                        </div>
                                    ) : (
                                        'Confirm cancellation'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Back to Game Button */}
                <div className='mt-8 text-center'>
                    <button
                        onClick={() =>
                            (window.location.href = `${
                                import.meta.env.VITE_REACT_APP_GAME_URL
                            }?t=${new Date().getTime()}`)
                        }
                        className='bg-gradient-to-r from-yellow-400 to-orange-500 !text-black py-3 px-8 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 font-medium'
                    >
                        Back to Puzzle Planet
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Account;
